<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import type { Tender } from '@/types/tender'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import AgentAssignmentModal from '../Modals/AgentAssignmentModal.vue'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import { notificationService } from '@/services/notificationService'
import CardComponent from '@/components/cards/CardComponent.vue'
import { useAuthStore } from '@/stores/auth'
import LabelText from '@/components/forms/LabelText.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import {EditIcon, RefreshIcon, EnvelopeIcon, MobileIcon, OctagonCheckIcon, BuildingIcon} from '@/icons' // ADD BuildingIcon import

const props = defineProps<{
  tender: Tender
}>()

const emit = defineEmits<{
  (e: 'tender-assigned', tenderId: string): void
  (e: 'refresh-tender'): void
}>()

const authStore = useAuthStore()
const isAdmin = computed(() => authStore.user?.userType === 'admin')

// Extended Tender interface with agent fields
interface ExtendedTender extends Tender {
  assignedAgentName?: string
  assignedAgentEmail?: string
  assignedAgentPhone?: string
  agentAssignedAt?: string
  assignedAt?: string
  agentNotes?: string
  agentName?: string
  company?: string  // ADD THIS - Company field
}

// Agent interface
interface Agent {
  id: string
  name: string
  email: string
  phone?: string
  first_name?: string
  last_name?: string
  username?: string
  company?: string  // ADD THIS - Company field
}

// Store all agents for lookup
const allAgents = ref<Agent[]>([])
const loadingAgents = ref(false)

// Local tender data that can be updated
const localTender = ref<ExtendedTender>({ ...props.tender as ExtendedTender })

// Watch for prop changes (in case parent refreshes)
watch(() => props.tender, (newTender) => {
  localTender.value = { ...newTender as ExtendedTender }
  console.log('🔄 Tender data updated from props:', localTender.value.assignedToAgent)
}, { deep: true })

// Add new state variables for individual tender deletion and agent assignment
const isAgentAssignmentModalOpen = ref(false)
const tenderToAssign = ref<ExtendedTender | null>(null)
const agentAssignmentModalRef = ref<InstanceType<typeof AgentAssignmentModal> | null>(null)
const isRefreshing = ref(false)

// Helper to get company display name
const getCompanyDisplay = (company: string | undefined): string => {
  if (!company) return 'No Company'

  const companies: Record<string, string> = {
    'maxstationary': 'Max Stationary',
    'ostnasik': 'OST Nasik',
    'shraddha-ent': 'Shraddha Enterprises'
  }

  return companies[company] || company
}

// Fetch all agents from API (same as AgentTender.vue)
const fetchAllAgents = async () => {
  if (!isAdmin.value) return // Only fetch agents if admin

  try {
    loadingAgents.value = true
    const response = await api.get<{success: boolean, agents: Agent[]}>('/api/users/agents')

    if (response.data.success && response.data.agents) {
      allAgents.value = response.data.agents.map((agent: Agent) => ({
        id: agent.id.toString(),
        name: `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || agent.username || agent.email,
        email: agent.email,
        phone: agent.phone,
        first_name: agent.first_name,
        last_name: agent.last_name,
        username: agent.username,
        company: agent.company, // ADD THIS - Map company field
      }))
      console.log(`✅ Loaded ${allAgents.value.length} agents for contact info`)
    }
  } catch (error) {
    console.error('Error fetching agents:', error)
  } finally {
    loadingAgents.value = false
  }
}

// Get agent details by ID
const getAgentDetails = (agentId: string): Agent | undefined => {
  if (!agentId) return undefined
  return allAgents.value.find(agent => agent.id === agentId.toString())
}

// Get agent name from tender
const assignedAgentName = computed(() => {
  return localTender.value.assignedAgentName ||
         localTender.value.agentName ||
         (localTender.value.assignedToAgent ? `Agent ${localTender.value.assignedToAgent.substring(0, 8)}...` : 'Not Assigned')
})

// Get agent company from tender
const assignedAgentCompany = computed(() => {
  let company = localTender.value.company

  // If company is not in tender data, try to get from agents list
  if (!company && localTender.value.assignedToAgent) {
    const agentDetails = getAgentDetails(localTender.value.assignedToAgent)
    company = agentDetails?.company
  }

  return company
})

// Get agent email (with fallback logic)
const assignedAgentEmail = computed(() => {
  let email = localTender.value.assignedAgentEmail

  // If email is not in tender data, try to get from agents list
  if (!email && localTender.value.assignedToAgent) {
    const agentDetails = getAgentDetails(localTender.value.assignedToAgent)
    email = agentDetails?.email
  }

  return email || 'Not available'
})

// Get agent phone (with fallback logic)
const assignedAgentPhone = computed(() => {
  let phone = localTender.value.assignedAgentPhone

  // If phone is not in tender data, try to get from agents list
  if (!phone && localTender.value.assignedToAgent) {
    const agentDetails = getAgentDetails(localTender.value.assignedToAgent)
    phone = agentDetails?.phone
  }

  return phone || 'Not available'
})

// Fetch updated tender data
const refreshTenderData = async () => {
  try {
    isRefreshing.value = true
    console.log('🔄 Refreshing tender data...')

    const response = await api.get<{success: boolean, tender: ExtendedTender}>(`/api/tenders/${localTender.value.t247Id}`)

    if (response.data.success && response.data.tender) {
      localTender.value = response.data.tender
      console.log('✅ Tender refreshed:', {
        assignedToAgent: localTender.value.assignedToAgent,
        assignedAgentName: localTender.value.assignedAgentName,
        company: assignedAgentCompany.value,
        email: assignedAgentEmail.value,
        phone: assignedAgentPhone.value
      })
    }
  } catch (error) {
    console.error('❌ Error refreshing tender:', error)
  } finally {
    isRefreshing.value = false
  }
}

// Open agent assignment modal
const openAgentAssignmentModal = () => {
  tenderToAssign.value = localTender.value
  isAgentAssignmentModalOpen.value = true
}

// Handle agent assignment from rejected tender
const handleAgentAssignment = async (assignmentData: {
  tenderId: string
  agentId: string
  agentName: string
  notes: string
}) => {
  try {
    console.log('🔄 Assigning agent to rejected tender:', assignmentData)
    console.log('🔍 DEBUG - agentName being sent to API:', assignmentData.agentName)

    // Get agent details for email, phone, and company
    const agentDetails = getAgentDetails(assignmentData.agentId)

    // FIRST: Change status from rejected to new (only if this is a rejected tender)
    if (localTender.value.status === 'rejected') {
      await api.put('/api/tenders/update-status', {
        tenderIds: [assignmentData.tenderId],
        status: 'new',
        rejectionComment: null,
        rejectedAt: null
      })
      console.log('✅ Status changed from rejected to new')
    }

    // SECOND: Assign agent using the same endpoint as Dashboard
    const response = await api.put('/api/tenders/assign-agent', {
      tenderId: assignmentData.tenderId,
      agentId: assignmentData.agentId,
      agentName: assignmentData.agentName,
      agentEmail: agentDetails?.email, // Add email
      agentPhone: agentDetails?.phone, // Add phone
      agentCompany: agentDetails?.company, // ADD THIS - Send company
      notes: assignmentData.notes,
      status: 'assigned-to-agent',
      workflowStatus: 'assigned-to-agent',
      assignedAt: new Date().toISOString()
    })

    console.log('✅ Assignment response:', response.data)

    if (response.data.success) {
      notificationService.success(`Agent "${assignmentData.agentName}" assigned successfully! Tender moved to workflow.`, 4000)
      isAgentAssignmentModalOpen.value = false

      // Refresh agents list to ensure we have latest data
      await fetchAllAgents()

      // Refresh the tender data to get updated agent info
      await refreshTenderData()

      // Emit event to parent
      emit('tender-assigned', localTender.value.t247Id)
      emit('refresh-tender')
    } else {
      throw new Error(response.data.error || 'Assignment failed')
    }

  } catch (error) {
    console.error('❌ Assignment error:', error)
    notificationService.error(`Failed to assign agent: ${getAxiosErrorMessage(error)}`)
  }
}

// Handle modal assign button click
const handleModalAssign = () => {
  if (agentAssignmentModalRef.value) {
    const success = agentAssignmentModalRef.value.submitForm()
    if (success) {
      // Modal will close automatically when handleAgentAssignment is called
    }
  }
}

// Cancel agent assignment
const handleCancelAssignment = () => {
  isAgentAssignmentModalOpen.value = false
  tenderToAssign.value = null
}

// Initialize - fetch agents when component is mounted
onMounted(() => {
  if (isAdmin.value) {
    fetchAllAgents()
  }
})

// Refresh button handler
const handleRefresh = async () => {
  await Promise.all([
    fetchAllAgents(),
    refreshTenderData()
  ])
}

const currentAgentId = computed(() => {
  return localTender.value.assignedToAgent || ''
})

const canChangeAgent = computed(() => {
  const restrictedStatuses = ['won', 'lost', 'assigned-to-engineer', 'completed'];
  return !restrictedStatuses.includes(localTender.value.status);
});
</script>

<template>
  <CardComponent
    v-if="isAdmin"
    title="Agent Assignment"
    :showHeaderBorder="true"
    :showHeaderPaddingBottom="true"
  >
    <template #header>
      <BaseButton
        :icon="RefreshIcon"
        size="xss"
        @click="handleRefresh"
      />
    </template>
    <template #body>
      <!-- Main loading state -->
      <LoaderDefault v-if="isRefreshing" title="Refreshing..." />

      <div v-else class="flex flex-col flex-1 gap-4">
        <!-- Agent Selection -->
        <div class="flex items-end gap-2">
          <TextInputDefault
            labelText="Assigned Agent"
            :model-value="assignedAgentName"
            type="text"
            placeholder="Select Agent"
            id="agent-id-input"
            disabled
            class="flex-1"
          />
          <!-- Hide button when tender status is in restricted list -->
          <BaseButton
            v-if="canChangeAgent"
            variant="secondary"
            :icon="EditIcon"
            :tooltip="localTender.assignedToAgent && !loadingAgents? 'Change Agent': 'Assign Agent'"
            tooltipPosition="bottom"
            @click="openAgentAssignmentModal"
            :disabled="loadingAgents"
          />
        </div>

        <LoaderDefault v-if="loadingAgents" title="Loading agent contact information..." />

        <!-- Current Agent Info - UPDATED to show company -->
        <div
          v-if="localTender.assignedToAgent && !loadingAgents"
          class="space-y-2"
        >
          <LabelText inputId="" labelText="Agent Info" />

          <div class="p-4 space-y-2 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-md">
            <!-- Company Info - NEW -->
            <p v-if="assignedAgentCompany" class="flex items-center gap-2">
              <BuildingIcon class="w-4 h-4 text-blue-600" />
              <span class="text-sm font-medium text-blue-700">
                {{ getCompanyDisplay(assignedAgentCompany) }}
              </span>
            </p>

            <p class="flex items-center gap-1">
              <EnvelopeIcon class="w-4 h-4" />
              <a
                v-if="assignedAgentEmail !== 'Not available'"
                :href="`mailto:${assignedAgentEmail}`"
                class="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate"
                :title="assignedAgentEmail"
              >
                {{ assignedAgentEmail }}
              </a>
              <span v-else class="text-sm text-gray-600">
                {{ assignedAgentEmail }}
              </span>
            </p>

            <p class="flex items-center gap-2 mb-2">
              <MobileIcon class="w-4 h-4" />
              <a
                v-if="assignedAgentPhone !== 'Not available' && assignedAgentPhone !== 'N/A'"
                :href="`tel:${assignedAgentPhone}`"
                class="text-sm text-green-600 hover:text-green-800 hover:underline"
              >
                {{ assignedAgentPhone }}
              </a>
              <span v-else class="text-sm text-gray-600">
                {{ assignedAgentPhone }}
              </span>
            </p>

            <!-- Assigned Date -->
            <p
              v-if="localTender.assignedAt || localTender.agentAssignedAt"
              class="pt-2 border-t border-blue-200 dark:border-blue-800 text-gray-500 dark:text-gray-400"
            >
              <span class="font-medium">Assigned on:</span>
              {{ new Date(localTender.assignedAt || localTender.agentAssignedAt!).toLocaleDateString() }}
              at
              {{ new Date(localTender.assignedAt || localTender.agentAssignedAt!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
            </p>
          </div>
        </div>

        <!-- Agent Notes -->
        <div v-if="props.tender.assignmentNotes" class="space-y-2">
          <LabelText inputId="" :labelText="`Agent notes for (${ assignedAgentName })`" />
          <div class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p class="text-sm text-gray-700 dark:text-gray-300">{{ props.tender.assignmentNotes }}</p>
          </div>
        </div>
      </div>
    </template>
  </CardComponent>

  <!-- Show assignment info for Agent(non-admin) users -->
  <CardComponent
    v-else
    title="Agent Information"
    :showHeaderBorder="true"
    :showHeaderPaddingBottom="true"
  >
    <template #body>
      <div class="flex-1 space-y-4">

        <LoaderDefault v-if="loadingAgents" title="Loading agent information..." />

        <div v-else class="space-y-2">
          <div class="flex items-center gap-2 mb-2 p-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded">
             <OctagonCheckIcon class="w-7 h-7" /> This tender is assigned to you {{ props.tender.assignedAgentName }}
          </div>

          <!-- Company Info - NEW for agent view -->
          <div v-if="assignedAgentCompany" class="flex items-center gap-2 mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
            <BuildingIcon class="w-4 h-4 text-gray-600" />
            <span class="text-sm text-gray-700 dark:text-gray-300">
              Company: {{ getCompanyDisplay(assignedAgentCompany) }}
            </span>
          </div>

          <!-- Assigned agent details -->
          <p
            v-if="localTender.assignedAt || localTender.agentAssignedAt"
            class="pt-2 text-gray-500 dark:text-gray-400"
          >
            <span class="font-medium">Assigned on:</span>
            {{ new Date(localTender.assignedAt || localTender.agentAssignedAt!).toLocaleDateString() }}
            at
            {{ new Date(localTender.assignedAt || localTender.agentAssignedAt!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
          </p>

          <!-- Agent Notes -->
          <div v-if="props.tender?.assignmentNotes" class="space-y-2 mt-4">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Tender assignment notes
            </label>
            <div class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p class="text-sm text-gray-700 dark:text-gray-300">{{ props.tender.assignmentNotes }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </CardComponent>

  <!-- Agent Assignment Modal -->
  <ModalDefault
    v-if="isAdmin && canChangeAgent"
    v-model="isAgentAssignmentModalOpen"
    title="Assign Tender to Agent"
    width="max-w-lg"
    classBody="my-4"
  >
    <AgentAssignmentModal
      v-if="tenderToAssign"
      ref="agentAssignmentModalRef"
      :tender="tenderToAssign"
      :currentAgentId="currentAgentId"
      @assign="handleAgentAssignment"
      @cancel="handleCancelAssignment"
    />

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="handleCancelAssignment"
        />
        <BaseButton
          text="Assign Tender"
          variant="primary"
          @click="handleModalAssign"
        />
      </div>
    </template>
  </ModalDefault>
</template>

<style scoped>
/* Toggle switch styles */
.dot {
  transition: all 0.3s ease-in-out;
}

/* Refresh button animation */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.transition-colors {
  transition: color 0.2s ease;
}
</style>
